
import { forwardRef, useImperativeHandle, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Form } from "@/components/ui/form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StudentFormHeader } from "./form/StudentFormHeader";
import { PersonalDataTab } from "./form/PersonalDataTab";
import { AssessmentTab } from "./form/AssessmentTab";
import { PreferencesTab } from "./form/PreferencesTab";
import { FinancialTab } from "./form/FinancialTab";
import { useStudentForm } from "./hooks/useStudentForm";
import { useStudentData } from "./hooks/useStudentData";
import { StudentFormValues } from "./schema/studentSchema";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslations } from "@/hooks/useTranslations";

interface StudentFormProps {
  initialData?: Partial<StudentFormValues> & { id?: string };
}

export const StudentForm = forwardRef<{ onSubmit: () => void }, StudentFormProps>(({ initialData }, ref) => {
  const { id } = useParams();
  const location = useLocation();
  const stateData = location.state;
  const { form, isSubmitting, onSubmit } = useStudentForm({ id, initialData: initialData as any });
  const { isLoading } = useStudentData(id, form);
  const isMobile = useIsMobile();
  const { t } = useTranslations("students");
  
  // Expose the onSubmit function to the parent component
  useImperativeHandle(ref, () => ({
    onSubmit
  }));

  useEffect(() => {
    // Priorizar initialData que vem como prop, depois o estado do location, principalmente
    // quando vem da página de pedidos de alunos
    const dataToUse = initialData || stateData;
    
    if (dataToUse && !id) {
      // Preencher os campos com dados iniciais (do pedido de aluno)
      const formValues = form.getValues();
      form.reset({
        ...formValues,
        first_name: dataToUse.first_name || '',
        last_name: dataToUse.last_name || '',
        email: dataToUse.email || '',
        phone: dataToUse.phone || '',
        goals: dataToUse.goals || '',
      });
    }
  }, [initialData, stateData, form, id]);

  if (isLoading) {
    return <div className="text-center py-4 dark:text-white">{t("form.loading")}</div>;
  }
  
  // Create a form wrapper that internally calls onSubmit
  const handleFormSubmit = () => {
    onSubmit();
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Only show the header if no ref is provided (standalone form mode) */}
      {!ref && (
        <StudentFormHeader 
          isSubmitting={isSubmitting}
          onSubmit={handleFormSubmit}
          isEditing={!!id}
        />
      )}

      <Form {...form}>
        <form className="space-y-4 md:space-y-6" onSubmit={(e) => e.preventDefault()}>
          <Tabs defaultValue="personal" className="w-full">
            <TabsList className={`grid w-full ${isMobile ? 'grid-cols-2 gap-1 mb-6' : 'grid-cols-4'} dark:bg-gray-700`}>
              <TabsTrigger value="personal" className="text-xs md:text-sm dark:data-[state=active]:bg-gray-600 dark:data-[state=active]:text-white dark:text-gray-300">{t("form.personalData")}</TabsTrigger>
              <TabsTrigger value="assessment" className="text-xs md:text-sm dark:data-[state=active]:bg-gray-600 dark:data-[state=active]:text-white dark:text-gray-300">{t("form.assessment")}</TabsTrigger>
              <TabsTrigger value="preferences" className="text-xs md:text-sm dark:data-[state=active]:bg-gray-600 dark:data-[state=active]:text-white dark:text-gray-300">{t("form.preferences")}</TabsTrigger>
              <TabsTrigger value="financial" className="text-xs md:text-sm dark:data-[state=active]:bg-gray-600 dark:data-[state=active]:text-white dark:text-gray-300">{t("form.financial")}</TabsTrigger>
            </TabsList>

            <TabsContent value="personal" className="p-1 md:p-0">
              <PersonalDataTab form={form} />
            </TabsContent>

            <TabsContent value="assessment" className="p-1 md:p-0">
              <AssessmentTab form={form} />
            </TabsContent>

            <TabsContent value="preferences" className="p-1 md:p-0">
              <PreferencesTab form={form} />
            </TabsContent>

            <TabsContent value="financial" className="p-1 md:p-0">
              <FinancialTab form={form} />
            </TabsContent>
          </Tabs>
        </form>
      </Form>
    </div>
  );
});

StudentForm.displayName = "StudentForm";
